import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types";
import { categories } from "@/mocks/categories";
import { formatViews } from "@/utils/video";

interface HeroCarouselProps {
  videos: Video[];
}

const heroImages = [
  "https://readdy.ai/api/search-image?query=Cinematic%20warm%20abstract%20cityscape%20background%20with%20soft%20orange%20and%20amber%20light%20streaks%20and%20gentle%20bokeh%2C%20artistic%20stylized%20atmosphere%2C%20dark%20elegant%20tones%20for%20text%20contrast%2C%20editorial%20hero%20photography%2C%20wide%20sweeping%20composition&width=1600&height=900&seq=hero-banner-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Cinematic%20deep%20ocean%20abstract%20background%20with%20glowing%20amber%20and%20teal%20particles%2C%20mysterious%20stylized%20mood%2C%20dark%20elegant%20tones%20for%20text%20contrast%2C%20artistic%20hero%20photography%2C%20wide%20atmospheric%20composition&width=1600&height=900&seq=hero-banner-02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Cinematic%20warm%20sunset%20terrace%20abstract%20background%20with%20soft%20orange%20haze%20and%20lens%20flare%2C%20relaxed%20stylized%20mood%2C%20dark%20elegant%20tones%20for%20text%20contrast%2C%20artistic%20hero%20photography%2C%20wide%20composition&width=1600&height=900&seq=hero-banner-03&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Cinematic%20colorful%20stage%20lights%20abstract%20background%20with%20warm%20orange%20and%20gold%20glow%2C%20energetic%20stylized%20mood%2C%20dark%20elegant%20tones%20for%20text%20contrast%2C%20artistic%20hero%20photography%2C%20wide%20composition&width=1600&height=900&seq=hero-banner-04&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Cinematic%20dynamic%20sports%20arena%20abstract%20background%20with%20warm%20amber%20light%20and%20motion%20blur%2C%20exciting%20stylized%20mood%2C%20dark%20elegant%20tones%20for%20text%20contrast%2C%20artistic%20hero%20photography%2C%20wide%20composition&width=1600&height=900&seq=hero-banner-05&orientation=landscape",
];

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const items = videos.slice(0, 5);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  };

  const current = items[index];
  const category = categories.find((c) => c.slug === current.category);
  const bg = heroImages[index % heroImages.length];

  return (
    <section className="relative w-full h-[440px] md:h-[580px] overflow-hidden bg-foreground-950">
      <img
        key={current.id}
        src={bg}
        alt={`${current.title} 焦点推荐大图`}
        title={`${current.title} - 免费视频`}
        className="absolute inset-0 w-full h-full object-cover object-top animate-fade-in"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />

      <div className="relative w-full h-full px-4 md:px-6 flex items-center">
        <div className="max-w-2xl animate-fade-in-up" key={`content-${current.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50">
              精选推荐
            </span>
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="px-2.5 py-1 text-xs font-medium rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/25 transition-colors"
              >
                {category.name}
              </Link>
            )}
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
            {current.title}
          </h2>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-background-100/90 line-clamp-2 max-w-xl">
            {current.description}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs md:text-sm text-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatViews(current.views)} 次观看
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              {current.duration}
            </span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-play-fill text-lg leading-none" />
              </span>
              立即观看
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 backdrop-blur-sm transition-colors whitespace-nowrap"
            >
              浏览更多
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 个推荐`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一个推荐"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一个推荐"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}